const SNAP_GRID_SIZE = 20;
const SNAP_THRESHOLD_PX = 6;
const DRAG_MIN_PX = 3;
const WELD_RADIUS_PX = 8;

function finiteNumber(value, fallback = 0) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
}

function normalizePoint(point = {}) {
  return {
    x: finiteNumber(point.x),
    y: finiteNumber(point.y),
  };
}

function distanceBetweenPoints(a = {}, b = {}) {
  const left = normalizePoint(a);
  const right = normalizePoint(b);
  return Math.sqrt((left.x - right.x) ** 2 + (left.y - right.y) ** 2);
}

function snapToGrid(value, gridSize = SNAP_GRID_SIZE, threshold = SNAP_THRESHOLD_PX) {
  const snapped = Math.round(finiteNumber(value) / gridSize) * gridSize;
  return Math.abs(finiteNumber(value) - snapped) <= threshold ? snapped : finiteNumber(value);
}

function snapPointToGrid(point, gridSize = SNAP_GRID_SIZE, threshold = SNAP_THRESHOLD_PX) {
  const normalized = normalizePoint(point);
  return {
    x: snapToGrid(normalized.x, gridSize, threshold),
    y: snapToGrid(normalized.y, gridSize, threshold),
  };
}

function sectionIdentity(section = {}) {
  return String(section.id || section.sectionId || section.secaoId || '').trim();
}

function collectSectionVertices(secoes = [], options = {}) {
  const excludeSectionId = String(options.excludeSectionId || '').trim();
  return (Array.isArray(secoes) ? secoes : [])
    .flatMap(section => {
      const currentSectionId = sectionIdentity(section);
      if (excludeSectionId && currentSectionId === excludeSectionId) return [];
      return (Array.isArray(section.pontos) ? section.pontos : [])
        .map((point, index) => ({
          ...normalizePoint(point),
          sectionId: currentSectionId,
          index,
        }));
    });
}

function findWeldPoint(point, vertices = [], radius = WELD_RADIUS_PX) {
  const normalized = normalizePoint(point);
  return (Array.isArray(vertices) ? vertices : [])
    .map(vertex => ({
      ...normalizePoint(vertex),
      distance: distanceBetweenPoints(normalized, vertex),
    }))
    .filter(candidate => candidate.distance <= radius)
    .sort((a, b) =>
      a.distance - b.distance ||
      a.x - b.x ||
      a.y - b.y
    )[0] || null;
}

function applyEditorPointPolicy(point, options = {}) {
  const raw = normalizePoint(point);
  const origin = options.origin ? normalizePoint(options.origin) : null;

  if (origin && distanceBetweenPoints(origin, raw) < (options.dragMinPx ?? DRAG_MIN_PX)) {
    return origin;
  }

  if (options.shiftKey) return raw;

  if (options.enableWeld !== false) {
    const vertices = Array.isArray(options.weldVertices)
      ? options.weldVertices
      : collectSectionVertices(options.secoes, { excludeSectionId: options.excludeSectionId });
    const welded = findWeldPoint(raw, vertices, options.weldRadiusPx ?? WELD_RADIUS_PX);
    if (welded) return { x: welded.x, y: welded.y };
  }

  if (options.enableSnap === false) return raw;
  return snapPointToGrid(raw, options.gridSize ?? SNAP_GRID_SIZE, options.snapThresholdPx ?? SNAP_THRESHOLD_PX);
}

function applySectionPointPolicy(point, options = {}) {
  return applyEditorPointPolicy(point, {
    ...options,
    enableSnap: false,
  });
}

export {
  SNAP_GRID_SIZE,
  SNAP_THRESHOLD_PX,
  DRAG_MIN_PX,
  WELD_RADIUS_PX,
  applyEditorPointPolicy,
  applySectionPointPolicy,
  collectSectionVertices,
  distanceBetweenPoints,
  findWeldPoint,
  snapPointToGrid,
  snapToGrid,
};
